import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ToolHolderRecord } from '@bridgewerk/shared';
import {
  getApiClient,
  type CheckinToolInput,
  type CheckoutToolInput,
  type CreateToolHolderInput,
  type EnrichedCheckout,
} from '../../../lib/api-client.js';
import { custodyKeys } from '../../../lib/query-keys.js';

export type { EnrichedCheckout, ToolHolderRecord };

export function useToolHolders(shopId: string | null) {
  return useQuery({
    queryKey: shopId ? custodyKeys.holders(shopId) : ['custody', 'none', 'holders'],
    queryFn:  () => getApiClient().listToolHolders(shopId!),
    enabled:  Boolean(shopId),
    staleTime: 60_000,
  });
}

export function useCreateToolHolder(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateToolHolderInput) => {
      if (!shopId) throw new Error('shopId required');
      return getApiClient().createToolHolder(shopId, input);
    },
    onSuccess: (created) => {
      if (!shopId) return;
      qc.setQueryData<ToolHolderRecord[]>(custodyKeys.holders(shopId), (prev) =>
        prev ? [created, ...prev] : [created],
      );
    },
  });
}

export function useOutstandingCheckouts(shopId: string | null) {
  return useQuery({
    queryKey: shopId ? custodyKeys.outstanding(shopId) : ['custody', 'none', 'outstanding'],
    queryFn:  () => getApiClient().getOutstandingCheckouts(shopId!),
    enabled:  Boolean(shopId),
    staleTime: 30_000,
  });
}

export function useCheckoutTool(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CheckoutToolInput) => {
      if (!shopId) throw new Error('shopId required');
      return getApiClient().checkoutTool(shopId, input);
    },
    onSuccess: () => {
      if (!shopId) return;
      void qc.invalidateQueries({ queryKey: custodyKeys.outstanding(shopId) });
    },
  });
}

export function useCheckinTool(shopId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CheckinToolInput) => {
      if (!shopId) throw new Error('shopId required');
      return getApiClient().checkinTool(shopId, input);
    },
    onSuccess: () => {
      if (!shopId) return;
      void qc.invalidateQueries({ queryKey: custodyKeys.outstanding(shopId) });
    },
  });
}
